import React, { useEffect, useRef } from "react"
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet"
import { divIcon } from "leaflet"

const pickerPinIcon = divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 40" width="28" height="40">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26S28 24.5 28 14C28 6.27 21.73 0 14 0z"
              fill="#e74c3c" stroke="white" stroke-width="1.5"/>
        <circle cx="14" cy="14" r="7" fill="white" opacity="0.9"/>
    </svg>`,
    className: "",
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
})

interface MapPosition {
    lat: number
    lng: number
}

interface Props {
    position: MapPosition | null
    onPositionChange: (pos: MapPosition) => void
}

const ClickHandler: React.FC<{ onPositionChange: (pos: MapPosition) => void }> = ({ onPositionChange }) => {
    useMapEvents({
        click(e) {
            onPositionChange({ lat: e.latlng.lat, lng: e.latlng.lng })
        },
    })
    return null
}

const InvalidateSize: React.FC = () => {
    const map = useMap()
    useEffect(() => {
        const t1 = setTimeout(() => map.invalidateSize(), 100)
        const t2 = setTimeout(() => map.invalidateSize(), 400)
        const t3 = setTimeout(() => map.invalidateSize(), 1000)

        const container = map.getContainer()
        let ro: ResizeObserver | null = null
        if (typeof ResizeObserver !== "undefined" && container) {
            ro = new ResizeObserver(() => map.invalidateSize())
            ro.observe(container)
        }

        return () => {
            clearTimeout(t1)
            clearTimeout(t2)
            clearTimeout(t3)
            ro?.disconnect()
        }
    }, [map])
    return null
}

const PanTo: React.FC<{ position: MapPosition }> = ({ position }) => {
    const map = useMap()
    const prevPos = useRef<MapPosition | null>(null)
    useEffect(() => {
        if (!position?.lat || !position?.lng) return
        if (
            !prevPos.current ||
            prevPos.current.lat !== position.lat ||
            prevPos.current.lng !== position.lng
        ) {
            map.panTo([position.lat, position.lng])
            prevPos.current = position
        }
    }, [position, map])
    return null
}

const LeafletGeoPickerMap: React.FC<Props> = ({ position, onPositionChange }) => {
    const defaultCenter: MapPosition = (position?.lat && position?.lng)
        ? position
        : { lat: 18.4718609, lng: -69.8923187 }

    return (
        <MapContainer
            center={[defaultCenter.lat, defaultCenter.lng]}
            zoom={15}
            style={{ width: "100%", height: "100%" }}
            scrollWheelZoom={true}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <InvalidateSize />
            <ClickHandler onPositionChange={onPositionChange} />
            {position?.lat && position?.lng && (
                <>
                    <Marker position={[position.lat, position.lng]} icon={pickerPinIcon} />
                    <PanTo position={position} />
                </>
            )}
        </MapContainer>
    )
}

export default LeafletGeoPickerMap
